import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import type { ApiKey, ApiKeyWithRawKey } from '@/types';

/**
 * Generate a cryptographically secure API key
 * Format: mm_<43-char base64url> = 46 chars total
 */
function generateApiKey(): string {
  const bytes = new Uint8Array(32); // 256 bits of entropy
  crypto.getRandomValues(bytes);
  const base64 = btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
  return `mm_${base64}`;
}

/**
 * Hash an API key using SHA-256
 */
async function hashApiKey(key: string): Promise<string> {
  const data = new TextEncoder().encode(key);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Fetch all API keys for the current user
 */
export function useApiKeys() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['api-keys', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('api_keys')
        .select('id, user_id, name, created_at, last_used_at, revoked_at')
        .eq('user_id', user.id)
        .is('revoked_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as ApiKey[];
    },
    enabled: !!user,
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Create a new API key
 * Returns the raw key ONCE - it cannot be retrieved again
 */
export function useCreateApiKey() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (name: string): Promise<ApiKeyWithRawKey> => {
      if (!user) throw new Error('Not authenticated');

      // Check if user already has an active key (limit: 1 per user)
      const { count, error: countError } = await supabase
        .from('api_keys')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .is('revoked_at', null);

      if (countError) throw countError;
      if (count && count >= 1) {
        throw new Error('每个用户只能有 1 个 API Key。请先删除现有的 Key。');
      }

      // Generate the raw key client-side
      const rawKey = generateApiKey();

      // Hash it before storing
      const keyHash = await hashApiKey(rawKey);

      const { data, error } = await supabase
        .from('api_keys')
        .insert({
          user_id: user.id,
          key_hash: keyHash,
          name: name || null,
        })
        .select('id, user_id, name, created_at, last_used_at, revoked_at')
        .single();

      if (error) throw error;

      // Return the data with the raw key (only time it's available!)
      return {
        ...data,
        raw_key: rawKey,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });
}

/**
 * Revoke an API key
 */
export function useRevokeApiKey() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (keyId: string) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('api_keys')
        .update({ revoked_at: new Date().toISOString() })
        .eq('id', keyId)
        .eq('user_id', user.id);

      if (error) throw error;
      return keyId;
    },
    onMutate: async (keyId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['api-keys', user?.id] });

      // Snapshot previous value
      const previousKeys = queryClient.getQueryData<ApiKey[]>(['api-keys', user?.id]);

      // Optimistically remove the key from the list
      queryClient.setQueryData<ApiKey[]>(['api-keys', user?.id], (old) =>
        old?.filter((k) => k.id !== keyId) ?? []
      );

      return { previousKeys };
    },
    onError: (_err, _keyId, context) => {
      // Rollback on error
      if (context?.previousKeys) {
        queryClient.setQueryData(['api-keys', user?.id], context.previousKeys);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });
}
